import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  LogIn,
  CreditCard,
  KeyRound,
  ShieldCheck,
  Zap,
  Plug,
  CheckCircle,
  MessageSquare,
  Smartphone,
  Megaphone,
  PhoneCall,
  Gamepad2,
  Antenna,
  ShoppingCart,
  Building,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import Seo from "@/components/Seo";


const useCases = [
  {
    title: "Login Authentication",
    description: "Require a second factor before granting account access.",
    Icon: LogIn,
    gradient: "icon-tile-teal",
    tags: ["SMS", "RCS"],
  },
  {
    title: "Transaction Authorization",
    description: "Confirm high-value or sensitive transactions with a one-time code.",
    Icon: CreditCard,
    gradient: "icon-tile-indigo",
    tags: ["SMS", "Voice"],
  },
  {
    title: "Account Changes",
    description: "Verify identity before password, email, or security setting changes.",
    Icon: KeyRound,
    gradient: "icon-tile-blue",
    tags: ["SMS"],
  },
  {
    title: "Admin Panel Access",
    description: "Protect internal tools and dashboards with an extra layer of authentication.",
    Icon: ShieldCheck,
    gradient: "icon-tile-orange",
    tags: ["SMS", "RCS"],
  },
];

const benefits = [
  {
    title: "Stronger Security",
    description: "Reduce account takeover with a verified second factor.",
    Icon: ShieldCheck,
    gradient: "icon-tile-teal",
  },
  {
    title: "Fast Delivery",
    description: "Codes arrive quickly so users aren't left waiting.",
    Icon: Zap,
    gradient: "icon-tile-indigo",
  },
  {
    title: "Easy Integration",
    description: "Add 2FA to existing login and transaction flows without a heavy lift.",
    Icon: Plug,
    gradient: "icon-tile-blue",
  },
];

const industries = [
  { title: "Fintech & Payments", to: "/industries/fintech-payments", Icon: CreditCard, gradient: "icon-tile-teal" },
  { title: "Digital Marketing Agencies", to: "/industries/digital-marketing-agencies", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "iGaming", to: "/industries/igaming", Icon: Gamepad2, gradient: "icon-tile-blue" },
  { title: "Telecom & CPaaS", to: "/industries/telecom-cpaas", Icon: Antenna, gradient: "icon-tile-orange" },
  { title: "Retail & Ecommerce", to: "/industries/retail-ecommerce", Icon: ShoppingCart, gradient: "icon-tile-pink" },
  { title: "Real Estate", to: "/industries/real-estate", Icon: Building, gradient: "icon-tile-green" },
];

const supportedChannels = [
  { title: "SMS", description: "Deliver 2FA codes reliably to any mobile number.", Icon: MessageSquare, gradient: "icon-tile-teal" },
  { title: "RCS", description: "Deliver richer, interactive 2FA experiences on supported devices.", Icon: Megaphone, gradient: "icon-tile-indigo" },
  { title: "Voice", description: "Read codes aloud for users who can't receive messages.", Icon: PhoneCall, gradient: "icon-tile-blue" },
];

const faqs = [
  { question: "What is two-factor authentication?", answer: "Two-factor authentication (2FA) adds a second verification step beyond a password, requiring users to confirm their identity with a one-time code sent to a trusted device. This extra layer makes it much harder for attackers to access accounts even if a password is compromised." },
  { question: "Which channels can deliver 2FA codes?", answer: "Yootelco delivers 2FA codes over SMS, RCS, and Voice. You can choose the channel that works best for your users or set up fallback routing between channels." },
  { question: "How is 2FA different from standard OTP verification?", answer: "Standard OTP verification typically confirms a single action, such as a login or transaction, with a one-time code. 2FA is specifically the practice of requiring that code as a second factor after the user has already provided something they know, like a password." },
  { question: "Can 2FA be added to an existing login flow?", answer: "Yes. 2FA can be integrated into existing login and transaction flows with a simple API call, so your current user experience stays intact while adding stronger security." },
  { question: "Is 2FA suitable for high-risk transactions as well as logins?", answer: "Yes. 2FA is commonly used for both login protection and high-risk transaction confirmation, such as large transfers, password changes, or admin actions." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const channelIcons = [
  { label: "SMS", Icon: MessageSquare },
  { label: "RCS", Icon: Megaphone },
];

const SolutionTwoFactorAuthentication = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="2FA API: SMS, RCS & Voice Authentication | Yootelco"
        description="Add a second layer of security with Yootelco's 2FA API. Send fast, reliable two-factor authentication codes over SMS, RCS, and Voice."
        path="/solutions/two-factor-authentication"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}>
        <div className="container">
          <div className="mx-auto max-w-[800px] text-center">
            <span className="inline-block rounded-full bg-primary/15 px-4 py-1.5 text-[13px] font-bold uppercase tracking-wider text-foreground">
              Two-Factor Authentication
            </span>
            <h1 className="mt-5 font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
              Two-Factor Authentication That Just Works
            </h1>
            <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
              Add a second layer of security to logins and transactions with fast, dependable codes over SMS and RCS.
            </p>
            <Link
              to="/contact"
              className="mt-[28px] inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-5">
              {channelIcons.map((channel) => (
                <div key={channel.label} className="flex items-center gap-2 text-[13px] font-semibold text-muted-foreground">
                  <channel.Icon className="h-4 w-4 text-primary" strokeWidth={2.2} />
                  {channel.label}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What Is Two-Factor Authentication */}
      <section className="section-shell pt-[20px] md:pt-[40px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">What Is Two-Factor Authentication?</h2>
          <p className="mt-[16px] text-[15px] leading-[1.65] text-muted-foreground md:text-[16px]">
            Two-factor authentication (2FA) adds a second verification step beyond a password, confirming a user's identity with a one-time code sent to a trusted device. It's one of the simplest ways to reduce account takeover and protect sensitive actions. Yootelco delivers 2FA codes reliably across SMS and RCS, so security doesn't come at the cost of user experience.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Use Cases</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {useCases.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col rounded-[18px] border border-border/60 bg-card px-6 py-7 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`mx-auto flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] flex-1 text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
                <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                  {item.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2.5 py-1 text-[11px] font-semibold text-foreground/80">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {/* Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Benefits</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {benefits.map((item) => {
            const Icon = item.Icon;
            return (
              <article
                key={item.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{item.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* Industries */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Industries We Serve</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {industries.map((item) => {
            const Icon = item.Icon;
            return (
              <Link
                key={item.title}
                to={item.to}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-card px-6 py-8 text-center shadow-soft transition-all duration-300 hover:-translate-y-1 hover:shadow-lift"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${item.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{item.title}</h3>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Stay Connected and Compliant */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[760px] text-center">
          <h2 className="section-title">Stay Connected and Compliant</h2>
          <div className="mx-auto mt-[24px] max-w-[640px] text-left">
            {[
              "Direct connections to mobile network operators",
              "Multi-channel delivery across SMS, RCS, and Voice",
              "Built for time-critical authentication traffic",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 py-3">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-primary/10 text-primary">
                  <CheckCircle className="h-3.5 w-3.5" strokeWidth={2.5} />
                </span>
                <p className="text-[15px] leading-[1.55] text-muted-foreground">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Supported Channels */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Supported Channels</h2>
        </div>
        <div className="mx-auto mt-[40px] grid max-w-[1120px] grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {supportedChannels.map((channel) => {
            const Icon = channel.Icon;
            return (
              <article
                key={channel.title}
                className="flex flex-col items-center rounded-[18px] border border-border/60 bg-secondary/40 px-6 py-8 text-center transition-all duration-300 hover:-translate-y-1"
              >
                <span className={`flex h-12 w-12 items-center justify-center rounded-[12px] ${channel.gradient}`}>
                  <Icon className="h-6 w-6" strokeWidth={2.2} />
                </span>
                <h3 className="mt-[18px] text-[17px] font-bold text-foreground">{channel.title}</h3>
                <p className="mt-[10px] text-[13.5px] leading-[1.6] text-muted-foreground">{channel.description}</p>
              </article>
            );
          })}
        </div>
      </section>

      {/* CTA Banner */}
            <CTASection
        title="Ready to Add Two-Factor Authentication?"
        description="Talk to our team about securing logins and transactions with 2FA."
        useCase="Two-Factor Authentication"
      />

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[800px] rounded-[36px] border border-border/70 bg-gradient-to-br from-secondary/60 to-card px-6 py-14 text-center shadow-soft md:px-10 md:py-20">
          <h2 className="section-title">Get Started with Two-Factor Authentication</h2>
          <Link to="/contact" className="mt-8 inline-flex rounded-xl bg-primary px-8 py-4 text-base font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift">
            Contact Sales
          </Link>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
};

export default SolutionTwoFactorAuthentication;
